import { Box, Typography, useTheme } from '@mui/material'
import { Doughnut } from 'react-chartjs-2'
import '@utils/chartSetup'

/**
 * A compact circular progress gauge for a single ratio metric (0–100%).
 *
 * The percentage and label are always rendered as text (never color-only), and
 * the whole widget exposes a descriptive `aria-label` via role="img", so the
 * gauge is meaningful to screen readers. When `value` is null/undefined the
 * gauge shows a muted "No data" state (honest empty state for metrics with no
 * underlying records yet).
 *
 * Props:
 *  - value:   number|null  percentage 0–100 (null → "No data")
 *  - label:   string       what the gauge measures (e.g. "PM compliance")
 *  - color:   palette key   'primary' | 'success' | 'info' | 'warning' | 'error'
 *  - caption: string        optional small line under the label
 *  - onClick: function      optional; makes the gauge a deep-link
 */
const RadialGauge = ({ value, label, color = 'primary', caption, onClick }) => {
  const theme = useTheme()
  const hasData = value !== null && value !== undefined && !Number.isNaN(value)
  const pct = hasData ? Math.max(0, Math.min(100, Math.round(value))) : 0
  const arcColor = theme.palette[color]?.main || theme.palette.primary.main
  const trackColor = theme.palette.divider

  const data = {
    labels: ['value', 'remainder'],
    datasets: [
      {
        data: hasData ? [pct, 100 - pct] : [0, 100],
        backgroundColor: [arcColor, trackColor],
        borderWidth: 0,
        circumference: 360,
        rotation: 0,
      },
    ],
  }
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: '72%',
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
  }

  const ariaLabel = hasData
    ? `${label} ${pct} percent${caption ? `, ${caption}` : ''}`
    : `${label}: no data`

  return (
    <Box
      role="img"
      aria-label={ariaLabel}
      onClick={onClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1,
        py: 1,
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: 2,
        transition: 'all 0.2s',
        '&:hover': onClick ? { transform: 'translateY(-2px)' } : {},
      }}
    >
      <Box sx={{ position: 'relative', width: 120, height: 120 }}>
        <Doughnut data={data} options={options} />
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {hasData ? (
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {pct}
              <Typography component="span" variant="body2" sx={{ fontWeight: 600 }}>
                %
              </Typography>
            </Typography>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
              No data
            </Typography>
          )}
        </Box>
      </Box>
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {label}
        </Typography>
        {caption && (
          <Typography variant="caption" color="text.secondary">
            {caption}
          </Typography>
        )}
      </Box>
    </Box>
  )
}

export default RadialGauge
